import { FC, useContext, useRef } from 'react';

import { Button, AddImageIcon } from '@smartshell/ui-kit';
import cn from 'classnames';

import { useUploadFile } from 'hooks';
import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';

import s from './ImageUploaderFieldMobile.module.scss';

interface IImageUploaderFieldProps {
  value: string;
  onChange: (value: string) => void;
  title?: string;
  disabled?: boolean;
}

const FILE_MAXIMUM_SIZE = 307200;

export const ImageUploaderFieldMobile: FC<IImageUploaderFieldProps> = ({
  value,
  onChange,
  title,
  disabled,
}) => {
  const { t } = useContext(LocalizationContext);
  const imgInput = useRef<HTMLInputElement>(null);

  const { uploadImage } = useUploadFile(onChange);

  return (
    <div className={cn(s.container, disabled && s.disabled)}>
      {value ? (
        <div className={s.wrapper}>
          <div
            style={{
              backgroundImage: `url(${value})`,
            }}
            className={s['image-container']}
          ></div>
          <div className={s['image-controls']}>
            <Button
              variant="secondary"
              color="red"
              className={s['delete-button']}
              onClick={() => onChange('')}
              onlyIcon
              disabled={disabled}
              iconName="BucketIcon"
            ></Button>

            <Button
              variant="secondary"
              onClick={() => imgInput.current?.click()}
              fullWidth
              disabled={disabled}
            >
              {t('change')}
            </Button>
          </div>
        </div>
      ) : (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        <div
          className={s['default-image']}
          onClick={() => {
            if (!disabled) {
              imgInput.current?.click();
            }
          }}
        >
          <AddImageIcon size="l" color={disabled ? 'disable' : 'link-info'} />
          {title}
        </div>
      )}
      <input
        ref={imgInput}
        type="file"
        accept=".png, .jpg"
        className={s['file-input']}
        onChange={async (event) => {
          const file = event.target.files?.[0];

          if (!file || file.size > FILE_MAXIMUM_SIZE) {
            if (event.target) event.target.value = '';
            return notification.info({
              t,
              type: 'error',
              title: t('upload.file.error'),
              message: t('upload.file.error_2'),
            });
          }

          try {
            const imageUrl = await uploadImage(file);
            onChange(imageUrl);
          } catch (error: unknown) {
            if (error instanceof Error) {
              notification.info({
                t,
                type: 'error',
                title: t('upload.file.error'),
                message: error?.message,
              });
            } else {
              notification.info({
                t,
                type: 'error',
                title: t('upload.file.error'),
                message: String(error),
              });
            }
          }
        }}
      />
    </div>
  );
};
